import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { useTenantData } from "contexts/tenant";
import { RootState, useAppDispatch, useAppSelector } from "store/store";
import { plotlyUtcMilliseconds } from "src/utils/plotlyDateAxis";

export type ScrapTrackingTimeZone = "UTC" | "local";

interface ScrapTrackingViewportState {
  startMs: number;
  endMs: number;
  timeZone: ScrapTrackingTimeZone;
}

const now = Date.now();

const initialState: ScrapTrackingViewportState = {
  startMs: now - 5 * 60 * 1000,
  endMs: now,
  timeZone: "UTC",
};

export const scrapTrackingViewportSlice = createSlice({
  name: "scrapTrackingViewport",
  initialState,
  reducers: {
    setRangeMs: (
      state,
      action: PayloadAction<{ startMs: number; endMs: number }>
    ) => {
      state.startMs = action.payload.startMs;
      state.endMs = action.payload.endMs;
    },
    setStartMs: (state, action: PayloadAction<number>) => {
      state.startMs = action.payload;
    },
    setEndMs: (state, action: PayloadAction<number>) => {
      state.endMs = action.payload;
    },
    setTimeZone: (state, action: PayloadAction<ScrapTrackingTimeZone>) => {
      state.timeZone = action.payload;
    },
    jumpBySeconds: (state, action: PayloadAction<number>) => {
      const deltaMs = Math.round(action.payload * 1000);
      state.startMs += deltaMs;
      state.endMs += deltaMs;
    },
    setWindowMs: (state, action: PayloadAction<number>) => {
      const durationMs = action.payload;
      state.startMs = state.endMs - durationMs;
    },
    resetToLastMs: (state, action: PayloadAction<number>) => {
      const durationMs = action.payload;
      const t = Date.now();
      state.endMs = t;
      state.startMs = t - durationMs;
    },
    centerOnTimestampMs: (state, action: PayloadAction<number>) => {
      const currentSize = state.endMs - state.startMs;
      const center = action.payload;
      state.startMs = Math.round(center - currentSize / 2);
      state.endMs = Math.round(center + currentSize / 2);
    },
  },
});

export const {
  setRangeMs,
  setStartMs,
  setEndMs,
  setTimeZone,
  jumpBySeconds,
  setWindowMs,
  resetToLastMs,
  centerOnTimestampMs,
} = scrapTrackingViewportSlice.actions;

const createAppSelector = createSelector.withTypes<RootState>();

export const selectScrapViewport = (state: RootState) =>
  state.scrapTrackingViewport;

export const selectScrapViewportStartMs = createAppSelector(
  [selectScrapViewport],
  (vp) => vp.startMs
);

export const selectScrapViewportEndMs = createAppSelector(
  [selectScrapViewport],
  (vp) => vp.endMs
);

export const selectScrapViewportStartDate = createAppSelector(
  [selectScrapViewportStartMs],
  (ms) => new Date(ms)
);

export const selectScrapViewportEndDate = createAppSelector(
  [selectScrapViewportEndMs],
  (ms) => new Date(ms)
);

export const selectScrapViewportRange = createAppSelector(
  [selectScrapViewportStartDate, selectScrapViewportEndDate],
  (start, end) => ({ start, end })
);

export const selectScrapViewportTimeZone = createAppSelector(
  [selectScrapViewport],
  (vp) => vp.timeZone
);

/**
 * Resolves the timezone preference ("UTC" | "local") to an actual timezone string.
 * "local" is resolved to the tenant's local_timezone.
 */
export const useResolvedScrapViewportTimeZone = () => {
  const preference = useAppSelector(selectScrapViewportTimeZone);
  const { local_timezone } = useTenantData();
  return preference === "local" ? local_timezone : "UTC";
};

export const useViewportRelayoutHandler = () => {
  const dispatch = useAppDispatch();

  return (event: Readonly<Plotly.PlotRelayoutEvent>) => {
    const range = event["xaxis.range"];
    const start = plotlyUtcMilliseconds(event["xaxis.range[0]"] ?? range?.[0]);
    const end = plotlyUtcMilliseconds(event["xaxis.range[1]"] ?? range?.[1]);
    if (Number.isFinite(start)) dispatch(setStartMs(start));
    if (Number.isFinite(end)) dispatch(setEndMs(end));
  };
};

export const useScrapTrackingViewport = () => {
  return useAppSelector(selectScrapViewportRange);
};
